import type { DeviceEnrollmentView, OutletSummary } from "@kopi/contracts";
import { DEFAULT_DEVICE_SORT, DEVICE_SORTS } from "@kopi/contracts/client";
import type { DevicePlatform } from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { ConfirmDialog } from "@kopi/ui/components/confirm-button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { useInfiniteQuery, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BanIcon, MonitorSmartphoneIcon, PlusIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { devices as messages } from "@/i18n/messages/devices";
import { useI18n, useMessages } from "@/i18n/provider";
import type { TranslateOf } from "@/i18n/translate";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { useConfirmTarget } from "@/lib/confirm-target";
import { devicesKey, devicesQuery, meScopesQuery, outletsQuery } from "@/lib/queries";
import { sortSearch, urlSorting } from "@/lib/sorting";

/** The column sort lives in the URL (R1-09d), so it survives a reload. */
const validateSearch = sortSearch(DEVICE_SORTS);

export const Route = createFileRoute("/_authed/devices")({
  staticData: { section: "workspace", crumb: "nav.devices" },
  validateSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: DevicesPage,
});

type T = TranslateOf<typeof messages.id>;

const PLATFORM_KEYS = {
  web: "devices.platform.web",
  android: "devices.platform.android",
  ios: "devices.platform.ios",
  desktop: "devices.platform.desktop",
} as const satisfies Record<DevicePlatform, keyof typeof messages.id>;

/** A date alone, in the interface language. */
function Day(props: { iso: string }) {
  const { format } = useI18n();
  return <time dateTime={props.iso}>{format.date(new Date(props.iso))}</time>;
}

const columnsFor = (t: T): DataTableColumn<DeviceEnrollmentView>[] => [
  {
    id: "register",
    header: t("devices.register"),
    meta: { card: "title" },
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{row.original.registerName}</span>
        <span className="truncate font-normal text-muted-foreground text-sm">{row.original.label}</span>
      </div>
    ),
  },
  {
    id: "platform",
    header: t("devices.platform"),
    cell: ({ row }) => t(PLATFORM_KEYS[row.original.platform]),
  },
  {
    id: "enrolledAt",
    header: t("devices.enrolledAt"),
    enableSorting: true,
    cell: ({ row }) => <Day iso={row.original.enrolledAt} />,
  },
  {
    id: "status",
    header: t("devices.status"),
    meta: { className: "md:w-32" },
    cell: ({ row }) =>
      row.original.revokedAt ? (
        <Badge variant="outline">{t("devices.revoked")}</Badge>
      ) : (
        <Badge variant="success">{t("devices.active")}</Badge>
      ),
  },
];

function DevicesPage() {
  const { t } = useMessages(messages);
  const columns = useMemo(() => columnsFor(t), [t]);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const queryClient = useQueryClient();
  const manageable = outlets.filter((o) => o.active && canAt(scopes, "devices.manage", o.id));
  // Starts at the shell's outlet when this user manages its devices.
  const [outletId, setOutletId] = useState(
    manageable.find((o) => o.id === current?.id)?.id ?? manageable[0]?.id ?? "",
  );
  const [enrolling, setEnrolling] = useState(false);
  const revoking = useConfirmTarget<DeviceEnrollmentView>();
  const { sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const devices = useInfiniteQuery(devicesQuery(outletId, sort));
  const sorting = urlSorting(DEVICE_SORTS, DEFAULT_DEVICE_SORT, sort, (next) => {
    void navigate({ search: next ? { sort: next } : {}, replace: true });
  });
  const outlet = manageable.find((o) => o.id === outletId);

  const revoke = useMutation({
    mutationFn: async (device: DeviceEnrollmentView) =>
      unwrap(await api.v1["device-enrollments"]({ enrollmentId: device.enrollmentId }).revoke.post()),
    onSuccess: async (_, device) => {
      await queryClient.invalidateQueries({ queryKey: devicesKey(device.outletId) });
      toast.success(t("devices.revokedToast", { name: device.registerName }));
    },
    onError: () => toast.error(t("devices.revokeFailed")),
  });

  if (manageable.length === 0) {
    return (
      <Page>
        <PageHeader title={t("devices.title")} subtitle={t("devices.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title={t("devices.title")}
        subtitle={t("devices.subtitle")}
        actions={
          outlet && (
            <Button type="button" onClick={() => setEnrolling(true)}>
              <PlusIcon />
              {t("devices.enrollThis")}
            </Button>
          )
        }
      />
      {scopes.device && (
        <p className="flex items-center gap-2 rounded-lg border bg-card p-4 text-sm">
          <MonitorSmartphoneIcon className="size-4 text-primary" />
          <span>
            {t("devices.registeredAs")} <strong>{scopes.device.registerName}</strong> ({scopes.device.label}).
          </span>
        </p>
      )}
      <DataTable
        paging="pages"
        label={t("devices.list")}
        columns={columns}
        query={devices}
        getRowId={(device) => device.enrollmentId}
        {...sorting}
        filters={
          // A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine).
          <OptionSelect
            aria-label={t("outlet.heading")}
            className="md:w-56"
            options={manageable.map((o) => ({ value: o.id, label: o.name }))}
            value={outletId}
            onValueChange={setOutletId}
          />
        }
        rowActions={(device) =>
          device.revokedAt
            ? []
            : [
                {
                  label: t("devices.revoke"),
                  icon: <BanIcon />,
                  destructive: true,
                  disabled: revoke.isPending,
                  onSelect: () => revoking.ask(device),
                },
              ]
        }
        empty={{
          title: t("devices.emptyTitle"),
          description: t("devices.emptyHint"),
        }}
      />
      {enrolling && outlet && <EnrollDialog outlet={outlet} onClose={() => setEnrolling(false)} />}
      <ConfirmDialog
        open={revoking.open}
        onOpenChange={revoking.onOpenChange}
        title={t("devices.revokeTitle", { name: revoking.target?.registerName ?? "" })}
        description={t("devices.revokeHint", { label: revoking.target?.label ?? "" })}
        confirmLabel={t("devices.revokeConfirm")}
        onConfirm={() => revoking.target && revoke.mutate(revoking.target)}
      />
    </Page>
  );
}

function EnrollDialog(props: { outlet: OutletSummary; onClose: () => void }) {
  const { t } = useMessages(messages);
  const queryClient = useQueryClient();
  const [registerName, setRegisterName] = useState(() => t("devices.defaultRegister"));
  const [label, setLabel] = useState("");
  const registerId = useId();
  const labelId = useId();
  const { platform } = Route.useRouteContext();

  const enroll = useMutation({
    mutationFn: async () =>
      unwrap(await api.v1.devices.enroll.post({ outletId: props.outlet.id, registerName, label, platform })),
    onSuccess: async () => {
      // Web: the credential is now an HttpOnly cookie on this device (never in the response body).
      // Native shells store the returned credential in secure storage (platform adapter, #35/#36).
      await queryClient.invalidateQueries({ queryKey: devicesKey(props.outlet.id) });
      // The session is now bound to this register: the top bar shows the cashier context.
      await queryClient.invalidateQueries({ queryKey: meScopesQuery.queryKey });
      toast.success(t("devices.enrolled"));
      props.onClose();
    },
  });
  const incomplete = !registerName.trim() || !label.trim();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!enroll.isPending && !incomplete) enroll.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("devices.enrollThis")}
      description={t("devices.enrollHint", {
        platform: t(PLATFORM_KEYS[platform]),
        outlet: props.outlet.name,
      })}
      dismissible={!enroll.isPending}
      footer={
        <Button type="submit" form="enroll-device" disabled={enroll.isPending || incomplete}>
          {t(enroll.isPending ? "devices.enrolling" : "devices.enroll")}
        </Button>
      }
    >
      <form id="enroll-device" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={registerId}>{t("devices.register")}</Label>
          <Input id={registerId} value={registerName} onChange={(e) => setRegisterName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={labelId}>{t("devices.label")}</Label>
          <Input
            id={labelId}
            placeholder={t("devices.labelPlaceholder")}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        {enroll.error && (
          <p role="alert" className="text-destructive text-sm">
            {t(
              enroll.error instanceof ApiRequestError && enroll.error.status === 404
                ? "devices.outletGone"
                : "devices.enrollFailed",
            )}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
